/**
 * Stay signed in with no service (operator, 2026-09-25: download a festival, airplane mode, force-quit, reopen ->
 * the app looked signed out and Sets / Maps were empty).
 *
 * Why: when the access token is older than an hour, auth-js's getSession() (and the INITIAL_SESSION event) try a
 * refresh first. With no network that refresh fails with AuthRetryableFetchError and auth-js answers session: null -
 * but it deliberately KEEPS the stored session (GoTrueClient._callRefreshToken / _recoverAndRefresh only call
 * _removeSession for non-retryable errors, @supabase/auth-js 2.95). So "stored session still in storage" is the
 * source of truth for "signed in": auth-js deletes it on a real auth failure (revoked / invalid refresh token, and
 * signOut), never on a network failure. When the network comes back, auth-js's refresh ticker (or our resume /
 * reconnect getSession call) refreshes it and emits TOKEN_REFRESHED, or SIGNED_OUT if the token was revoked.
 *
 * The client is created with the default storage (window.localStorage) and the default storageKey
 * (sb-<project-ref>-auth-token, supabase-js SupabaseClient), so this read is synchronous. If supabaseClient.js ever
 * passes a custom auth.storageKey or storage, change getAuthStorageKey / readJson to match.
 *
 * No import of supabaseClient on purpose: this stays a leaf module (unit-testable, no client side effects).
 */

/** The key auth-js persists the session under - supabase-js's default for our project URL. */
export function getAuthStorageKey(supabaseUrl = import.meta.env?.VITE_SUPABASE_URL) {
  try {
    if (!supabaseUrl) return null;
    return `sb-${new URL(supabaseUrl).hostname.split('.')[0]}-auth-token`;
  } catch {
    return null;
  }
}

function readJson(key) {
  if (!key || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * The persisted session if it can still be refreshed (has a refresh token and a user id), else null.
 * The access token may be expired - that is the point; callers use it for identity, not for requests.
 */
export function readStoredAuthSession(storageKey = getAuthStorageKey()) {
  const stored = readJson(storageKey);
  if (!stored || typeof stored !== 'object') return null;
  if (!stored.refresh_token || !stored.access_token) return null;
  let user = stored.user;
  if (!user?.id) {
    // userStorage split (auth-js keeps the user under <key>-user when configured).
    user = readJson(`${storageKey}-user`)?.user ?? null;
  }
  if (!user?.id) return null;
  return { ...stored, user };
}

/** Synchronous user id for the boot cache key. */
export function readStoredAuthUserId() {
  return readStoredAuthSession()?.user?.id ?? null;
}

/**
 * The session the app should act on: auth-js's when it has one; otherwise the stored session, unless this is a real
 * sign-out. Use for getSession() results and onAuthStateChange events alike.
 */
export function resolveAuthSession(session, { event } = {}) {
  if (session?.user?.id) return session;
  if (event === 'SIGNED_OUT') return null;
  return readStoredAuthSession();
}
